import { Colors } from "@/enums/Colors";
import { EnumsUtils } from "@/utils/TaskEnumUtils";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Fade, Popper, SxProps, Typography, styled } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

interface Option {
  value: string;
  render: React.ReactNode;
}

interface Props<T> {
  options?: Option[];
  onChange?: (value: string) => void;
  disabled?: boolean;
  currentValue?: string;
  disableHoverTransition?: boolean;
  name: string;
  sx?: SxProps;
  listContainerSx?: SxProps;
  customValueRender?: React.ReactNode;
  customOnClick?: boolean;
  iconOnlyOnClick?: boolean;
  border?: boolean;
  placeholder?: string;
}

export const CommonDropdown = <T,>(props: Props<T>) => {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);
  const valueContainerRef = useRef<HTMLDivElement | null>(null);

  const canBeOpen = open && Boolean(anchor);
  const id = canBeOpen ? `${props.name}-popper-${props.name}` : undefined;

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleClickOutside = (event: MouseEvent) => {
    if (popperRef.current && !popperRef.current.contains(event.target as Node)) {
      setOpen(false);
    }
  };

  const onClickInput = () => {
    setAnchor(valueContainerRef?.current);
    setOpen(true);
  };

  const onClickOption = (value: string) => {
    props.onChange(value);
    setOpen(false);
  };

  return (
    <Box sx={{ ...props.sx }}>
      <ValueContainer
        ref={valueContainerRef}
        disableHoverTransition={props.disableHoverTransition}
        open={open}
        className="select-input"
        disabled={props.disabled}
        onClick={props.disabled ? undefined : !props.iconOnlyOnClick ? onClickInput : undefined}
        iconOnlyOnClick={props.iconOnlyOnClick}
        sx={{ border: props.border && `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`, cursor: props.disabled ? "auto" : "pointer" }}
      >
        {!props.currentValue && props.placeholder && <Typography sx={{ color: Colors.DARK_GRAY }}>{props.placeholder}</Typography>}
        {props.customValueRender || <Typography>{EnumsUtils.parseEnumToRender(props.currentValue) || "-"}</Typography>}

        <Box height={"1rem"} sx={{ cursor: "pointer" }} onClick={() => (props.iconOnlyOnClick ? onClickInput() : undefined)}>
          {open ? <ExpandLess /> : <ExpandMore className="icon" sx={{ display: !props.disableHoverTransition && "none" }} />}
        </Box>
      </ValueContainer>

      <Popper
        id={id}
        open={open}
        anchorEl={anchor}
        transition
        placement="bottom"
        ref={popperRef}
        sx={{ zIndex: 2200 }}
        onMouseLeave={() => {
          setOpen(false);
        }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Container
              sx={{
                ...props.listContainerSx,
                width: valueContainerRef?.current?.offsetWidth,
              }}
              className="root-container"
            >
              <Container className="list-container">
                {props.options.map((opt) => {
                  return (
                    <Container
                      className="list-item"
                      sx={{ cursor: "pointer" }}
                      onClick={() => !props.customOnClick && onClickOption(opt.value)}
                      key={opt.value}
                    >
                      {opt.render}
                    </Container>
                  );
                })}
              </Container>
            </Container>
          </Fade>
        )}
      </Popper>
    </Box>
  );
};

const ValueContainer = styled(Box)<{
  open: boolean;
  disabled?: boolean;
  disableHoverTransition?: boolean;
  iconOnlyOnClick?: boolean;
}>(({ open, disabled, disableHoverTransition, iconOnlyOnClick }) => ({
  cursor: iconOnlyOnClick ? "auto" : "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexDirection: "row",
  fontSize: "12px",
  fontWeight: "400",
  padding: "0.875rem 0.250rem",
  width: "100%",
  height: "2rem",
  backgroundColor: "inherit",
  transition: !disableHoverTransition && "border-color 0.3s, background-color 0.3s",
  border: disableHoverTransition ? `1px solid ${Colors.STROKE_DARK_GRAY}` : "1px solid transparent",
  borderColor: disableHoverTransition ? Colors.STROKE_DARK_GRAY : open ? `${Colors.STROKE_FOR_HOVERS_6C6C77}` : "transparent",
  borderRadius: "0.375rem",
  "& p": {
    padding: "5px",
  },
  "&:hover": {
    border: !disabled && `1px solid ${Colors.STROKE_FOR_HOVERS_6C6C77}`,
    "-moz-appearance": "textfield",
    backgroundColor: "#fff",
    "& .icon": {
      display: !disabled && "inline",
    },
  },
}));

const Container = styled(Box)(() => ({
  "&.root-container": {
    marginTop: "0.25rem",
    backgroundColor: "white",
    borderRadius: "0.5rem",
    boxShadow: " 0px 0px 15px 0px #00000033",
  },
  "&.list-container": {
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    justifyContent: " space-between",
    "& > div:last-child": {
      borderBottomLeftRadius: "0.5rem",
      borderBottomRightRadius: "0.5rem",
    },
    "& > div:first-child": {
      borderTopLeftRadius: "0.5rem",
      borderTopRightRadius: "0.5rem",
    },
    "& > div": {
      backgroundColor: "inherit",
      transition: "background-color 0.3s",
      paddingBlock: "5px",
      paddingInline: "10px",
      "&:hover": {
        cursor: "pointer",
        backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
      },
    },
  },
}));
